import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, CheckCircle, XCircle, Clock, Loader2, TrendingDown, TrendingUp, AlertTriangle, IndianRupee } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffMyAttendancePage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [stats, setStats] = useState({ present: 0, absent: 0, late: 0, leave: 0, total: 0 });
  const [deductionSummary, setDeductionSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  // Generate last 6 months for selection
  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    return months;
  };

  useEffect(() => {
    fetchAttendance();
  }, [selectedMonth]);

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      // Try to get the monthly report with deduction summary
      const response = await axios.get(`${API_URL}/api/attendance/staff/report/${user?.id}?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const records = response.data?.records || [];
      setAttendance(records);
      setDeductionSummary(response.data?.deduction_summary || null);
      
      // Use summary from API if available
      if (response.data?.summary) {
        const s = response.data.summary;
        setStats({ 
          present: s.present || 0, 
          absent: s.absent || 0, 
          late: s.late_days || 0, 
          leave: s.on_leave || 0, 
          total: s.total_days || 0,
          halfDay: s.half_day || 0,
          totalLateMinutes: s.total_late_minutes || 0,
          totalOvertimeMinutes: s.total_overtime_minutes || 0
        });
      } else {
        // Calculate stats manually
        const present = records.filter(r => r.status === 'present').length;
        const absent = records.filter(r => r.status === 'absent').length;
        const late = records.filter(r => r.is_late).length;
        const leave = records.filter(r => r.status === 'on_leave').length;
        setStats({ present, absent, late, leave, total: records.length });
      }
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
      // Fallback to basic attendance API
      try {
        const fallbackRes = await axios.get(`${API_URL}/api/hr/staff-attendance/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const records = fallbackRes.data || [];
        setAttendance(records);
        const present = records.filter(r => r.status === 'present').length;
        const absent = records.filter(r => r.status === 'absent').length;
        const late = records.filter(r => r.status === 'late' || r.is_late).length;
        const leave = records.filter(r => r.status === 'on_leave').length;
        setStats({ present, absent, late, leave, total: records.length });
      } catch (e) {
        // Silent fail
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'absent': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'late': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'on_leave': return <Calendar className="h-4 w-4 text-purple-500" />;
      default: return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      present: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      absent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      late: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      on_leave: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <DashboardLayout title="My Attendance">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const attendanceRate = stats.total > 0 ? Math.round((stats.present / stats.total) * 100) : 0;

  return (
    <DashboardLayout title="My Attendance">
      <div className="space-y-6">
        {/* Month Selector */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Monthly Attendance Report</h2>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent>
              {getMonthOptions().map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Total Days</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Present</p>
                  <p className="text-2xl font-bold text-green-600">{stats.present}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Absent</p>
                  <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">On Leave</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.leave}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">Attendance %</p>
                  <p className={`text-2xl font-bold ${attendanceRate >= 75 ? 'text-green-600' : 'text-red-600'}`}>
                    {attendanceRate}%
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Deduction Summary Card */}
        {deductionSummary && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Card className="border-amber-200 dark:border-amber-900">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <IndianRupee className="h-5 w-5 text-amber-600" />
                  Salary Deduction Summary
                </CardTitle>
                <CardDescription>Based on attendance for {selectedMonth}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Late Days Info */}
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-medium">Late Days</span>
                    </div>
                    <p className="text-xl font-bold">{stats.late || 0}</p>
                    <p className="text-xs text-muted-foreground">
                      Excess: {deductionSummary.excess_late_days || 0} days
                    </p>
                  </div>
                  
                  {/* Late Deduction */}
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingDown className="h-4 w-4 text-red-600" />
                      <span className="text-sm font-medium">Late Deduction</span>
                    </div>
                    <p className="text-xl font-bold text-red-600">
                      ₹{deductionSummary.late_deduction || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ₹{deductionSummary.settings_applied?.late_deduction_per_day || 50}/day
                    </p>
                  </div>
                  
                  {/* Half Days */}
                  <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="h-4 w-4 text-orange-600" />
                      <span className="text-sm font-medium">Half Days</span>
                    </div>
                    <p className="text-xl font-bold">{deductionSummary.half_days_total || 0}</p>
                    <p className="text-xs text-muted-foreground">
                      From late: {deductionSummary.late_converted_to_half_days || 0}
                    </p>
                  </div>
                  
                  {/* Overtime Earnings */}
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium">Overtime</span>
                    </div>
                    <p className="text-xl font-bold text-green-600">
                      +₹{deductionSummary.overtime_earnings || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {deductionSummary.overtime_hours || 0} hours
                    </p>
                  </div>
                </div>
                
                {/* Settings Info */}
                <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm">
                  <p className="font-medium mb-1">Applied Settings:</p>
                  <div className="flex flex-wrap gap-4 text-muted-foreground">
                    <span>Max late days allowed: {deductionSummary.settings_applied?.max_late_days_allowed || 3}</span>
                    <span>Late to half-day: {deductionSummary.settings_applied?.late_count_for_half_day || 3} late = 1 half-day</span>
                    <span>OT rate: ₹{deductionSummary.settings_applied?.overtime_rate_per_hour || 100}/hr</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Attendance Records */}
        <Card>
          <CardHeader>
            <CardTitle>Attendance History</CardTitle>
          </CardHeader>
          <CardContent>
            {attendance.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No attendance records found</p>
            ) : (
              <div className="space-y-2">
                {attendance.slice(0, 30).map((record, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(record.status)}
                      <span className="font-medium">{record.date}</span>
                      {(record.check_in_time || record.check_in) && (
                        <span className="text-sm text-muted-foreground">
                          In: {record.check_in_time || record.check_in} 
                          {(record.check_out_time || record.check_out) && ` | Out: ${record.check_out_time || record.check_out}`}
                          {record.late_minutes > 0 && <span className="text-amber-600 ml-2">(Late: {record.late_minutes}m)</span>}
                          {record.overtime_minutes > 0 && <span className="text-green-600 ml-2">(OT: {record.overtime_minutes}m)</span>}
                        </span>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(record.status)}`}>
                      {record.status?.replace('_', ' ').charAt(0).toUpperCase() + record.status?.replace('_', ' ').slice(1)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StaffMyAttendancePage;
